import { FC } from "react";
import { Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { CustomIconButton } from "../../../components/CustomIconButton/CustomIconButton";
import { colors } from "../../../styles/colors";
import useStyles from "./styles";
import { RosterRow } from "../../../hooks/useCollaboratorRoster";
import {
  useResendInvitation,
  useRevokeInvitation,
} from "../../../api/invitation";
import { getCurrentFleetId } from "../../../utils/getCurrentFleet";

/**
 * Row actions, which differ by roster status.
 *
 * A confirmed member gets "open sheet" (and "open vehicle" when one is
 * assigned). An outstanding invitation gets "resend" and "revoke" instead:
 * there is no sheet to open yet, because no `company_user` row exists until
 * the invitee completes signup.
 *
 * CustomIconButton takes no `disabled` prop, so in-flight state is expressed
 * on the wrapper (pointer-events + opacity) rather than by swapping in a
 * different button component.
 */
const ActionButton: FC<{
  label: string;
  iconName: string;
  backgroundColor: string;
  busy?: boolean;
  onClick: () => void;
}> = ({ label, iconName, backgroundColor, busy, onClick }) => (
  <Tooltip title={label} placement="top">
    <span
      style={{
        display: "inline-flex",
        opacity: busy ? 0.5 : 1,
        pointerEvents: busy ? "none" : "auto",
      }}
    >
      <CustomIconButton
        iconName={iconName}
        widthIcon={"30px"}
        heightIcon={"30px"}
        backgroundColor={backgroundColor}
        onClick={onClick}
      />
    </span>
  </Tooltip>
);

export const ButtonsActionsCollaborator: FC<{ collaborator: RosterRow }> = ({
  collaborator,
}) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const companyId = getCurrentFleetId();

  const resend = useResendInvitation(companyId);
  const revoke = useRevokeInvitation(companyId);

  if (collaborator.status !== "member") {
    const code = collaborator.invitationCode;
    // Revoked invitations are terminal: resending one would contradict the
    // manager's own decision to cancel it. Invite again instead.
    const canResend = collaborator.status !== "revoked" && !!code;

    return (
      <div className={classes.buttonsCollab}>
        {canResend && (
          <ActionButton
            label={t("collaborator.resendInvitation", "Relancer l'invitation")}
            iconName="send"
            backgroundColor={colors.primary}
            busy={resend.isPending}
            onClick={() => code && resend.mutate(code)}
          />
        )}
        {collaborator.status === "pending" && !!code && (
          <ActionButton
            label={t("collaborator.revokeInvitation", "Révoquer l'invitation")}
            iconName="trash"
            backgroundColor={colors.error}
            busy={revoke.isPending}
            onClick={() => code && revoke.mutate(code)}
          />
        )}
      </div>
    );
  }

  return (
    <div className={classes.buttonsCollab}>
      {collaborator.vehicleId && (
        <ActionButton
          label={t("collaborator.openVehicle", "Ouvrir le véhicule")}
          iconName="vehicule"
          backgroundColor={colors.primary}
          onClick={() => navigate(`/vehicule/update/${collaborator.vehicleId}`)}
        />
      )}
      <ActionButton
        label={t("collaborator.openSheet", "Ouvrir la fiche")}
        iconName="collaborateur"
        backgroundColor={colors.secondary}
        onClick={() => navigate(`/collaborator/update/${collaborator.userId}`)}
      />
    </div>
  );
};
